import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import ColoredTitle from "./ColoredTitle";

const FULL_TEXT = "where to next?";
const NAV_LINKS = [
  { label: "PROJECTS", to: "/projects" },
  { label: "CONTACT", to: "/contact" },
];

function WhereNext() {
  const ref = useRef(null);
  const [typed, setTyped] = useState("");
  const [showLinks, setShowLinks] = useState(false);
  const [started, setStarted] = useState(false);

  // Start typing when section bottom comes into view
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started) setStarted(true);
      },
      { threshold: 0.6 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [started]);

  // Typewriter
  useEffect(() => {
    if (!started) return;
    let idx = 0;
    const tick = setInterval(() => {
      idx++;
      setTyped(FULL_TEXT.slice(0, idx));
      if (idx === FULL_TEXT.length) {
        clearInterval(tick);
        setTimeout(() => setShowLinks(true), 400);
      }
    }, 65);
    return () => clearInterval(tick);
  }, [started]);

  return (
    <div ref={ref} className="flex flex-col items-center gap-6 pt-16 pb-4">
      {/* Typewriter line */}
      <p className="font-semibold text-lg sm:text-xl md:text-2xl tracking-wide">
        {/* Already-typed chars — lightBrown */}
        <span className="text-lightBrown">
          {typed.length === FULL_TEXT.length ? typed : typed.slice(0, -1)}
        </span>
        {/* Currently-typing char — pink bg + deep (only while still typing) */}
        {typed.length > 0 && typed.length < FULL_TEXT.length && (
          <span className="text-deep bg-pink px-0.5 rounded">
            {typed[typed.length - 1]}
          </span>
        )}
        {/* Blinking cursor — classic sharp on/off */}
        <span className="cursor-blink text-deep bg-pink px-0.5 rounded ml-0.5">
          |
        </span>
      </p>

      {/* Links fade in after typing */}
      <AnimatePresence>
        {showLinks && (
          <motion.div
            className="flex gap-6 sm:gap-10"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          >
            {NAV_LINKS.map((l, i) => (
              <motion.div
                key={l.to}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.12, duration: 0.4 }}
              >
                <Link
                  to={l.to}
                  className="text-xs sm:text-sm font-bold tracking-widest text-midBrown
                    hover:text-lightBrown transition-colors duration-200
                    border-b border-midBrown/40 hover:border-lightBrown pb-0.5"
                >
                  {l.label} ↗
                </Link>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const EDUCATION = [
  {
    period: "2023 — 2025",
    degree: "Master of Science in Computer Science",
    school: "Boston University",
    detail: "",
  },
  {
    period: "2018 — 2022",
    degree: "Bachelor of Science in Management Information Systems",
    school: "University of Delaware",
    detail: "Minor in Japanese and Asian Study",
  },
];

const CERTIFICATIONS = [
  {
    name: "ServiceNow Certified System Administrator (CSA)",
    url: "https://www.credly.com/badges/185abf57-a860-4711-bad7-d0ea531f49ee/public_url",
  },
  {
    name: "ServiceNow Certified Application Developer (CAD)",
    url: "https://www.credly.com/badges/fc0f6045-9aa8-409f-aa2a-55bca5934925/public_url",
  },
];

export default function EducationSection() {
  return (
    <section
      id="education"
      className="min-h-screen px-4 sm:px-8 md:px-12 lg:px-20 xl:px-28
        pt-16 pb-20 sm:pt-20 md:pt-16 lg:pt-20"
    >
      {/* Sticky title */}
      <div className="sticky top-0 z-20 bg-lightBeige pt-16 pb-3 transition-shadow duration-300 -mx-4 sm:-mx-8 md:-mx-12 lg:-mx-20 xl:-mx-28 px-4 sm:px-8 md:px-12 lg:px-20 xl:px-28 mb-10 sm:mb-12 md:mb-16 text-center">
        <h2 className="font-extrabold text-4xl sm:text-5xl md:text-6xl lg:text-7xl">
          <ColoredTitle text="Education." />
        </h2>
      </div>

      {/* Divider */}
      <div className="flex justify-center mb-8 sm:mb-10 md:mb-14">
        <div className="h-[3px] bg-lightBrown rounded-full w-28 sm:w-36 md:w-48 lg:w-64"></div>
      </div>

      {/* Timeline */}
      <motion.div
        initial={{ opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.1 }}
        transition={{ duration: 0.55, ease: "easeOut" }}
        className="flex flex-col gap-0"
      >
        {EDUCATION.map((edu, i) => (
          <div key={i} className="flex flex-col sm:flex-row gap-0">
            {/* Left: date + vertical line */}
            <div className="flex sm:flex-col sm:items-center sm:w-48 md:w-56 lg:w-64 shrink-0">
              <div className="flex items-center gap-2 sm:gap-0 sm:flex-col sm:items-center">
                <span className="text-lightBrown text-base sm:text-lg shrink-0">
                  ✶
                </span>
                <p className="text-xs sm:text-sm font-semibold text-midBrown whitespace-nowrap sm:mt-1 ml-2 sm:ml-0">
                  {edu.period}
                </p>
              </div>
              {i < EDUCATION.length - 1 && (
                <div className="hidden sm:block w-[2px] flex-1 bg-lightBrown/40 mx-auto mt-2 min-h-[8rem]"></div>
              )}
            </div>

            {/* Right: content */}
            <div className="pl-6 sm:pl-8 md:pl-10 pb-14 sm:pb-16 border-l-2 border-lightBrown/40 sm:border-none mt-1 sm:mt-0">
              <h3
                className="font-bold mb-1
                text-xl sm:text-2xl md:text-3xl lg:text-4xl"
              >
                {edu.degree}
              </h3>
              <p
                className="font-semibold italic text-brown mb-2 sm:mb-3
                text-base sm:text-lg md:text-xl"
              >
                {edu.school}
              </p>
              <p
                className="leading-relaxed text-midBrown
                text-sm sm:text-base md:text-base lg:text-lg"
              >
                {edu.detail}
              </p>
            </div>
          </div>
        ))}

        {/* Certifications — same timeline row layout, links to Credly */}
        <div className="flex flex-col sm:flex-row gap-0">
          <div className="flex sm:flex-col sm:items-center sm:w-48 md:w-56 lg:w-64 shrink-0">
            <div className="flex items-center gap-2 sm:gap-0 sm:flex-col sm:items-center">
              <span className="text-lightBrown text-base sm:text-lg shrink-0">
                ✶
              </span>
              <p className="text-xs sm:text-sm font-semibold text-midBrown whitespace-nowrap sm:mt-1 ml-2 sm:ml-0">
                ServiceNow
              </p>
            </div>
          </div>

          <div className="pl-6 sm:pl-8 md:pl-10 pb-14 sm:pb-16 border-l-2 border-lightBrown/40 sm:border-none mt-1 sm:mt-0">
            <h3
              className="font-bold mb-2 sm:mb-3
              text-xl sm:text-2xl md:text-3xl lg:text-4xl"
            >
              Certifications
            </h3>
            <ul className="flex flex-col gap-2">
              {CERTIFICATIONS.map((cert) => (
                <li key={cert.url}>
                  <a
                    href={cert.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold italic text-brown border-b border-lightBrown/50 pb-0.5
                      hover:text-lightBrown hover:border-lightBrown transition-colors duration-200
                      text-base sm:text-lg md:text-xl"
                  >
                    {cert.name} ↗
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </motion.div>

      <WhereNext />
    </section>
  );
}
